import type { Vec2 } from '../geo/types';
import type { BoatState, ControlState, ControlEnv, InputFrame, Pose, SettledMode } from './types';
import { createWalkState, updateWalk } from './walk';
import { boatEye, launch, moored, updateBoat } from './boat';
import { riseToFlight } from './rise';
import { createFlyState, updateFly } from './fly';
import { createFlyEntry, flyEntryTarget, interpolateFlyEntry } from './flyEntry';
import {
  createTransition,
  updateTransition,
  isTransitionComplete,
  interpolatePose,
} from './transition';
import {
  BOAT,
  FLY,
  PLAYER,
  TRANSITION,
  WALK_DESCENT_ALTITUDE_M,
  CAMERA,
  FOV_RESTORE_TIME_S,
} from '../core/config';
import { damp } from '../core/math';
import { layoutToWorld, worldToLayout } from '../core/coords';

export function createControlState(
  spawn: Vec2,
  mode: SettledMode,
  env: ControlEnv,
  bearing = 0
): ControlState {
  // A site with no water to float a hull near the spawn starts on foot.
  const boat = mode === 'boat' ? launch(env.water, spawn, bearing) : null;
  if (boat) {
    return {
      mode: 'boat',
      pose: { position: boatEye(boat), yaw: boat.bearing, pitch: 0, roll: 0, fovYDeg: 60 },
      velocity: [0, 0, 0],
      flySpeed: FLY.defaultSpeed,
      transition: null,
      atPainterView: false,
      boat,
    };
  }
  if (mode !== 'fly') {
    const walkState = createWalkState(spawn, (p) => env.groundHeightAt(p));
    return {
      mode: 'walk',
      pose: walkState.pose,
      velocity: walkState.velocity,
      flySpeed: FLY.defaultSpeed,
      transition: null,
      atPainterView: false,
      boat: null,
    };
  }

  const groundY = env.groundHeightAt(spawn);
  const position = layoutToWorld(spawn, groundY + FLY.minClearance + 10);
  const pose: Pose = {
    position,
    yaw: 0,
    pitch: 0,
    roll: 0,
    fovYDeg: 60,
  };
  const flyState = createFlyState(pose);

  return {
    mode: 'fly',
    pose: flyState.pose,
    velocity: flyState.velocity,
    flySpeed: flyState.flySpeed,
    transition: null,
    atPainterView: false,
    boat: null,
  };
}

function hasMovementInput(input: InputFrame): boolean {
  return (
    input.forward !== 0 ||
    input.right !== 0 ||
    input.up !== 0 ||
    input.lookDx !== 0 ||
    input.lookDy !== 0
  );
}

export function updateControls(
  state: ControlState,
  input: InputFrame,
  dt: number,
  env: ControlEnv
): ControlState {
  if (state.mode === 'transition') {
    return updateTransitionMode(state, input, dt, env);
  }

  const wantsPainterView = env.allowFlight !== false && input.actions.includes('painterView');
  const wantsToggleMode = env.allowFlight !== false && input.actions.includes('toggleMode');

  if (wantsPainterView) {
    return state.atPainterView ? state : startPainterTransition(state, env);
  }

  if (input.actions.includes('toggleBoat')) {
    return state.mode === 'boat' ? stepAshore(state, env) : board(state, env);
  }

  if (wantsToggleMode) {
    return toggleMode(state, env);
  }

  if (state.mode === 'walk') {
    return updateWalkMode(state, input, dt, env);
  }

  if (state.mode === 'boat' && state.boat) {
    return updateBoatMode(state, state.boat, input, dt, env);
  }

  return updateFlyMode(state, input, dt, env);
}

function updateWalkMode(
  state: ControlState,
  input: InputFrame,
  dt: number,
  env: ControlEnv
): ControlState {
  const walkState = updateWalk(
    { pose: state.pose, velocity: state.velocity },
    input,
    dt,
    env.collision,
    (p) => env.groundHeightAt(p)
  );

  const newFovYDeg = state.atPainterView
    ? walkState.pose.fovYDeg
    : damp(walkState.pose.fovYDeg, CAMERA.defaultFovYDeg, FOV_RESTORE_TIME_S, dt);

  return {
    ...state,
    pose: { ...walkState.pose, fovYDeg: newFovYDeg },
    velocity: walkState.velocity,
  };
}

function updateBoatMode(
  state: ControlState,
  boat: BoatState,
  input: InputFrame,
  dt: number,
  env: ControlEnv
): ControlState {
  const rowed = updateBoat(boat, state.pose, input, dt, env.water, env.swellAt);
  const [x, , z] = rowed.pose.position;
  return {
    ...state,
    boat: rowed.boat,
    pose: {
      ...rowed.pose,
      fovYDeg: damp(rowed.pose.fovYDeg, CAMERA.defaultFovYDeg, FOV_RESTORE_TIME_S, dt),
    },
    velocity:
      dt > 0
        ? [(x - state.pose.position[0]) / dt, 0, (z - state.pose.position[2]) / dt]
        : [0, 0, 0],
  };
}

/* A step from one place to another: across at once where the two are a few strides apart, and
   carried over in a low arc where they are not. */
function passTo(state: ControlState, pose: Pose, mode: SettledMode): ControlState {
  const from = state.pose.position;
  const distance = Math.hypot(
    pose.position[0] - from[0],
    pose.position[1] - from[1],
    pose.position[2] - from[2]
  );
  if (distance <= BOAT.stepM) {
    return { ...state, mode, pose, velocity: [0, 0, 0], transition: null, atPainterView: false };
  }
  return {
    ...state,
    mode: 'transition',
    velocity: [0, 0, 0],
    transition: {
      ...createTransition(state.pose, pose, mode),
      arcLiftM: Math.min(TRANSITION.arcLiftM, distance / 4),
    },
    atPainterView: false,
  };
}

/* Into the boat: the one left moored if it lies near, or else one on the nearest water. */
function board(state: ControlState, env: ControlEnv): ControlState {
  const here = worldToLayout(state.pose.position);
  const near =
    state.boat &&
    Math.hypot(state.boat.position[0] - here[0], state.boat.position[1] - here[1]) <= BOAT.hailM;
  const boat = near && state.boat ? state.boat : launch(env.water, here, state.pose.yaw);
  if (!boat) return state;
  const pose: Pose = {
    position: boatEye(boat),
    yaw: boat.bearing,
    pitch: 0,
    roll: 0,
    fovYDeg: state.pose.fovYDeg,
  };
  return passTo({ ...state, boat: moored(boat) }, pose, 'boat');
}

/* Out of the boat onto the nearest ground a walker can stand on. The boat stays where it lies. */
function stepAshore(state: ControlState, env: ControlEnv): ControlState {
  const ground = env.collision.nearestWalkable(worldToLayout(state.pose.position));
  const pose: Pose = {
    ...state.pose,
    position: layoutToWorld(ground, env.groundHeightAt(ground) + PLAYER.eyeHeight),
    roll: 0,
  };
  return passTo({ ...state, boat: state.boat && moored(state.boat) }, pose, 'walk');
}

function updateFlyMode(
  state: ControlState,
  input: InputFrame,
  dt: number,
  env: ControlEnv
): ControlState {
  /*
   * The world opens at the painter view, 3 km out. The first movement input glides in to the fly start
   * over the ground the painter looked at, rather than damping the lens in place: widening the lens
   * where the camera stands shrinks the city, which read as the view zooming out (flyEntry.ts).
   */
  if (state.atPainterView && hasMovementInput(input)) {
    return startFlyEntry(state, env);
  }

  const flyState = updateFly(
    { pose: state.pose, velocity: state.velocity, flySpeed: state.flySpeed },
    input,
    dt,
    (p) => env.groundHeightAt(p)
  );

  const newAtPainterView = state.atPainterView && !hasMovementInput(input);

  const newFovYDeg = newAtPainterView
    ? flyState.pose.fovYDeg
    : damp(flyState.pose.fovYDeg, CAMERA.defaultFovYDeg, FOV_RESTORE_TIME_S, dt);

  return {
    ...state,
    pose: { ...flyState.pose, fovYDeg: newFovYDeg },
    velocity: flyState.velocity,
    flySpeed: flyState.flySpeed,
    atPainterView: newAtPainterView,
  };
}

function updateTransitionMode(
  state: ControlState,
  _input: InputFrame,
  dt: number,
  _env: ControlEnv
): ControlState {
  if (!state.transition) return state;

  const updated = updateTransition(state.transition, dt);

  if (isTransitionComplete(updated)) {
    return {
      ...state,
      mode: updated.targetMode,
      pose: updated.to,
      velocity: [0, 0, 0],
      transition: null,
      atPainterView: updated.endsAtPainterView,
    };
  }

  const pose = updated.glide ? interpolateFlyEntry(updated) : interpolatePose(updated);

  return {
    ...state,
    pose,
    transition: updated,
  };
}

/** The glide from the painter view in to the fly start, on the painter's own bearing. */
function startFlyEntry(state: ControlState, env: ControlEnv): ControlState {
  return {
    ...state,
    mode: 'transition',
    velocity: [0, 0, 0],
    transition: createFlyEntry(state.pose, flyEntryTarget(state.pose, env.spawn)),
    atPainterView: false,
  };
}

function startPainterTransition(state: ControlState, env: ControlEnv): ControlState {
  const fromPose = state.mode === 'fly' ? state.pose : toFlyPose(state, env);
  const transition = createTransition(fromPose, env.painterPose, 'fly', true);

  return {
    ...state,
    mode: 'transition',
    pose: fromPose,
    transition,
    atPainterView: false,
    boat: state.boat && moored(state.boat),
  };
}

function toFlyPose(state: ControlState, env: ControlEnv): Pose {
  const layoutPos = worldToLayout(state.pose.position);
  const groundY = env.groundHeightAt(layoutPos);
  const minY = groundY + FLY.minClearance;
  const newY = Math.max(state.pose.position[1], minY);

  return {
    ...state.pose,
    position: [state.pose.position[0], newY, state.pose.position[2]],
  };
}

function toggleMode(state: ControlState, env: ControlEnv): ControlState {
  if (state.mode === 'walk' || state.mode === 'boat') {
    // A boat's roll on the waves is not carried into flight.
    const flyState = createFlyState({ ...state.pose, roll: 0 }, state.flySpeed);
    const flying: ControlState = {
      ...state,
      mode: 'fly',
      pose: flyState.pose,
      velocity: flyState.velocity,
      flySpeed: flyState.flySpeed,
      boat: state.boat && moored(state.boat),
    };
    return state.mode === 'boat' ? riseToFlight(flying, env) : flying;
  }

  const layoutPos = worldToLayout(state.pose.position);
  const groundY = env.groundHeightAt(layoutPos);
  const altitudeAboveGround = state.pose.position[1] - groundY;

  if (altitudeAboveGround > WALK_DESCENT_ALTITUDE_M) {
    return startSpawnTransition(state, env);
  }

  const walkablePos = env.collision.nearestWalkable(layoutPos);
  const walkableGroundY = env.groundHeightAt(walkablePos);
  const position = layoutToWorld(walkablePos, walkableGroundY + PLAYER.eyeHeight);

  return {
    ...state,
    mode: 'walk',
    pose: {
      ...state.pose,
      position,
      roll: 0,
    },
    velocity: [0, 0, 0],
    atPainterView: false,
  };
}

function startSpawnTransition(state: ControlState, env: ControlEnv): ControlState {
  const spawnGroundY = env.groundHeightAt(env.spawn);
  const targetPosition = layoutToWorld(env.spawn, spawnGroundY + PLAYER.eyeHeight);

  const targetPose: Pose = {
    position: targetPosition,
    yaw: 0,
    pitch: 0,
    roll: 0,
    fovYDeg: CAMERA.defaultFovYDeg,
  };

  const transition = createTransition(state.pose, targetPose, 'walk');

  return {
    ...state,
    mode: 'transition',
    transition,
    atPainterView: false,
  };
}
